import { type FC, useEffect, useMemo } from 'react';
import type { FormItemProps, SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { uniqBy } from 'es-toolkit';
import i18n from 'i18next';

import type { RoutingCity } from '@/views/screens/RoutingGuide/types';

import FormSelectBox from '../../../../components/Inputs/FormSelectBox';
import { useGetCityList } from '../../hooks';

interface CitySelectProps extends Pick<FormItemProps, 'rules' | 'name'>, Pick<SelectProps, 'mode' | 'placeholder'> {
  label?: string;
  countryCode?: string;
  stateCode?: string;
  editMode?: boolean;
  margin?: string;
  className?: string;
  // TODO: refactor to use only RoutingCity type for CitySelect
  selectedCity?: (RoutingCity & { label: string; value: string })[];
  callbackAllOptions?: (options: DefaultOptionType[]) => void;
  isRequired?: boolean;
}

export const CitySelect: FC<CitySelectProps> = ({
  placeholder = i18n.t('translation:default.fields.city.placeholder'),
  label = i18n.t('translation:default.fields.city.label'),
  countryCode,
  stateCode,
  rules,
  name,
  margin = 'm-0',
  editMode,
  mode,
  className = '',
  callbackAllOptions,
  selectedCity = [],
  isRequired = false,
}) => {
  const { data: cityOptions = [], isLoading } = useGetCityList({
    CountryCode: countryCode,
    isDisabled: !stateCode?.trim(),
    StateCode: stateCode,
  });

  const options = useMemo(() => {
    const uniqOptions = uniqBy(cityOptions, (item) => item.value);

    if (selectedCity?.length > 0) {
      const findState = selectedCity.filter((i) => i.stateCode.trim() === stateCode).map((i) => i.value);
      return uniqOptions.filter((i) => findState.includes(i.value));
    }
    return uniqOptions;
  }, [cityOptions, selectedCity.filter, selectedCity.length, stateCode]);

  useEffect(() => {
    callbackAllOptions?.(options);
  }, [options, callbackAllOptions]);

  return (
    <FormSelectBox
      loading={isLoading}
      options={options}
      placeholder={placeholder}
      label={label}
      margin={margin}
      rules={rules}
      name={name}
      mode={mode}
      editMode={editMode}
      className={className}
      isRequired={isRequired}
    />
  );
};
